import { useCallback, useEffect, useRef, useState } from "react";

interface UseStoryTimerProps {
  currentIndex: number;
  duration: number;
  videoRef?: React.RefObject<HTMLVideoElement>;
  onComplete: () => void;
  isPlaying?: boolean;
}

const useStoryTimer = ({
  currentIndex,
  duration,
  videoRef,
  onComplete,
  isPlaying = true,
}: UseStoryTimerProps) => {
  const [progress, setProgress] = useState(0);
  const intervalIdRef = useRef<number | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);

  const stopTimer = useCallback(() => {
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
      intervalIdRef.current = null;
    }
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    stopTimer();

    if (videoRef?.current) {
      const video = videoRef.current;
      const updateProgress = () => {
        setProgress((video.currentTime / video.duration) * 100);
      };
      video.addEventListener("timeupdate", updateProgress);
      cleanupRef.current = () =>
        video.removeEventListener("timeupdate", updateProgress);
    } else {
      intervalIdRef.current = setInterval(() => {
        setProgress((prev) => {
          if (prev >= 100) {
            stopTimer();
            onComplete();
            return 0;
          }
          return prev + 100 / (duration * 10);
        });
      }, 100);
    }
  }, [duration, videoRef, onComplete, stopTimer]);

  useEffect(() => {
    setProgress(0);
    if (isPlaying) {
      startTimer();
    }
    return stopTimer;
  }, [currentIndex, isPlaying, startTimer, stopTimer]);

  return { progress };
};

export default useStoryTimer;
